import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvLink,
	EvSection,
	EvSpinner,
	EvTitle,
	IFilterValues,
	TABLE_FILTERS_SR_SUMMARY
} from '@evinced-private/ui-common';

import {
	MFA_DASHBOARD_PAGE_FILTERS_SECTION_ID,
	MFA_DASHBOARD_PAGE_MAIN_TITLE_ID,
	MFA_DASHBOARD_PAGE_OLDER_SESSIONS_LINK_ID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import omit from 'lodash-es/omit';
import { MfaSessionsPageFilterDefinitions } from 'src/apps/mfa/helpers/MfaSessionsPageFilterDefinitionsHelper';
import { useMfaLabelsOptionsQuery } from 'src/apps/mfa/hooks/useMfaLabelsOptionsQuery';
import { useMfaOwnerOptionsQuery } from 'src/apps/mfa/hooks/useMfaOwnerOptionsQuery';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { MFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';

import {
	PLATFORM_OPTIONS,
	SESSIONS_PERIOD_OPTIONS,
	SEVERITY_OPTIONS
} from './MfaSessionsFilterDefinitions';

import './MfaSessionsHeader.scss';

const MFA_PAGE_TITLE = 'All sessions';

interface MfaSessionsPageHeaderProps {
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
}

const EMPTY_CREATED_BY_OPTION = { value: ' ', label: 'Unknown' };

export const MfaSessionsPageHeader: FC<MfaSessionsPageHeaderProps> = ({
	filters,
	onFilterChange
}) => {
	const ownerOptionsQuery = useMfaOwnerOptionsQuery();
	const labelOptionsQuery = useMfaLabelsOptionsQuery();

	const initialValues = useMemo(() => {
		const selectFilters = omit(filters, 'searchValue');

		return {
			text: { searchValue: filters?.searchValue },
			select: selectFilters
		};
	}, [filters]);

	const filterOptions = useMemo(() => {
		const staticFilterOptions = {
			sessionsPeriod: SESSIONS_PERIOD_OPTIONS,
			platform: PLATFORM_OPTIONS,
			severity: SEVERITY_OPTIONS,
			owner:
				ownerOptionsQuery.data?.map((item) => {
					if (typeof item.value === 'string' && item.value === ' ') {
						return EMPTY_CREATED_BY_OPTION;
					}
					return item;
				}) ?? []
		};

		const dynamicFilterOptions = {};

		labelOptionsQuery.data?.forEach((label) => {
			dynamicFilterOptions[label.labelKey] = label.options;
		});

		return { ...staticFilterOptions, ...dynamicFilterOptions };
	}, [ownerOptionsQuery.data, labelOptionsQuery.data]);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(MFA_SESSIONS_TABLE_ID, flatFilters);
	};

	const isLoading = ownerOptionsQuery.isLoading || labelOptionsQuery.isLoading;

	return (
		<div className="mfa-sessions-header">
			<div className="main-title-container">
				<EvTitle
					titleText={MFA_PAGE_TITLE}
					dataAutomationId={MFA_DASHBOARD_PAGE_MAIN_TITLE_ID}
					headingLevel={1}
					className="main-title page-heading"
					MoreInfoContent={
						<>
							Reports are available for the Mobile Flow Analyzer version 9.0.0 and newer. To see
							reports from previous Mobile Flow Analyzer versions, go to the Old Platform.
						</>
					}
				/>
				<EvLink
					url={RoutesHelper.getMfaV1DashboardPath()}
					linkText="See older Sessions"
					showIcon
					dataAutomationId={MFA_DASHBOARD_PAGE_OLDER_SESSIONS_LINK_ID}
				/>
			</div>
			<EvSection
				ariaLabel="Mobile Flow Analyzer filters"
				ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
				ariaLive="polite"
				skipLinkId="filters-section"
				className="dashboard-section"
				dataAutomationId={MFA_DASHBOARD_PAGE_FILTERS_SECTION_ID}
			>
				{!isLoading ? (
					<EvFilters
						className="data-filters"
						onApply={handleApply}
						onReset={handleApply}
						initialValues={initialValues}
						filterOptions={filterOptions}
						filterDefinitions={MfaSessionsPageFilterDefinitions}
						isFilterOptionsLoaded={!isLoading}
						isInitialValuesLoaded={!isLoading}
					/>
				) : (
					<EvSpinner />
				)}
			</EvSection>
		</div>
	);
};
